import React, { useState, useEffect, KeyboardEvent, useCallback } from "react"
import { observer } from "mobx-react"
import { Screen, Table, TableProperty, FilterButtons, DeactivateButtons } from "../components"
import { makeStyles } from "@material-ui/styles"
import { useStores } from "../models/root-store"
import { useHistory } from "react-router-dom"
import { SearchBar } from "../components/Layout"
import { CommonAttributes } from "eteva-types"
import { RootPaths, SubPaths } from "../routers/Paths"
import { CategoryModelType } from "../models/category-store"

const useStyles = makeStyles({
  container: {
    padding: "20px 50px",
    display: "flex",
    flexDirection: "column",
    maxWidth: 1400,
    width: "100%",
    flex: 1,
    msFlexDirection: "column",
  },
  filterContainer: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    margin: "26px 0",
  },
})

const tableProperties: TableProperty<CategoryModelType>[] = [
  { key: "title", title: "categories.title" },
  { key: "active", title: "categories.active" },
]

const filterButtons = [
  { value: true, label: "categories.activeFilter" },
  { value: false, label: "categories.inactiveFilter" },
]

export const CategoriesScreen: React.FC = observer(() => {
  const {
    categoryStore: { categories, getAll, total, fetching, changeItemStatuses, deleteOne },
  } = useStores()
  const { container, filterContainer } = useStyles()
  const history = useHistory()
  const [currentPage, setCurrentPage] = useState(0)
  const [selectedRadio, setSelectedRadio] = useState<boolean | undefined>()
  const [selectedItems, setSelectedItems] = useState<number[]>([])
  const [search, setSearch] = useState("")
  const pageSize = 10

  const getAllCategories = async (page = 0) => {
    if (!page) setCurrentPage(0)
    setSelectedRadio(undefined)
    setSelectedItems([])
    await getAll(page, pageSize, search, selectedRadio)
  }

  const memoizedGetCategories = useCallback(getAllCategories, [])

  useEffect(() => {
    memoizedGetCategories()
  }, [memoizedGetCategories])

  const handleEditClick = (id: number) => {
    history.push(`${RootPaths.categories}${SubPaths.edit}/${id}`)
  }

  const handleSearch = (e: KeyboardEvent<HTMLInputElement>) => {
    const searchQuery = (e.target as HTMLInputElement).value || ""
    setSearch(searchQuery)
    setCurrentPage(0)
    setSelectedItems([])
    getAll(0, pageSize, searchQuery, selectedRadio)
  }

  const handleSelectOne = (id: number) => {
    const alreadySelected = selectedItems.indexOf(id) >= 0
    if (alreadySelected) return setSelectedItems(selectedItems.filter(it => it !== id))
    setSelectedItems([...selectedItems, id])
  }

  const handleSelectAll = () => {
    if (selectedItems.length === categories.length) return setSelectedItems([])
    setSelectedItems([...categories.map(it => it.id)])
  }

  const handleUpdateActivityClick = async (newStatus: boolean) => {
    const res = await changeItemStatuses(selectedItems, newStatus)
    if (res) await memoizedGetCategories()
  }

  const handleDeleteActivityClick = async () => {
    if (selectedItems.length === 0) {
      return;
    }
  
    const deletionPromises = selectedItems.map(async (itemId) => {
      await deleteOne([itemId]);
    });
  
    try {
      await Promise.all(deletionPromises);
      setSelectedItems([]);
      await memoizedGetCategories();
    } catch (error) {
      console.error("Error deleting items:", error);
    }
  }
  

  const onRadioClick = (value: boolean) => {
    setSelectedRadio(value)
    setCurrentPage(0)
    setSelectedItems([])
    getAll(0, pageSize, search, value)
  }

  const onChangePage = (event: any, page: number) => {
    if (total > pageSize * page || page > -1) {
      setCurrentPage(page)
      getAll(page, pageSize, search, selectedRadio)
    }
  }

  const renderFilters = () => (
    <div className={filterContainer}>
      {selectedItems?.length ? (
        <DeactivateButtons
          path="categories"
          onActivateClick={() => handleUpdateActivityClick(true)}
          onDeactivateClick={() => handleUpdateActivityClick(false)}
          onDeleteClick={() => handleDeleteActivityClick()}
        />
      ) : (
        <>
          <FilterButtons
            onRadioClick={onRadioClick}
            buttons={filterButtons}
            selected={selectedRadio}
            defaultChecked="super"
          />
          <SearchBar width="100%" onChange={handleSearch} placeholder="categories.searchByName" />
        </>
      )}
    </div>
  )

  return (
    <Screen headerTitle="screens.categories" createButton>
      <div className={container}>
        {renderFilters()}
        <Table<CategoryModelType & CommonAttributes>
          loading={fetching}
          total={total}
          page={currentPage}
          pageSize={pageSize}
          onChangePage={onChangePage}
          onEditClick={handleEditClick}
          items={categories}
          properties={tableProperties}
          edit
          selectedItems={selectedItems}
          onSelectOne={handleSelectOne}
          onSelectAll={handleSelectAll}
        />
      </div>
    </Screen>
  )
})
